/**
 * ICS (iCalendar) File Generator
 * Generates .ics files for calendar imports
 */

import { Reminder } from '../types/reminder';

/**
 * Generate ICS file content from reminder data
 */
export function generateICSContent(reminder: Reminder): string {
  const now = new Date();
  const reminderDateTime = new Date(`${reminder.date}T${reminder.time}`);
  
  // Format dates for ICS (YYYYMMDDTHHMMSS)
  const formatICSDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  // Generate unique identifier
  const uid = `${Date.now()}@luciddock.app`;
  
  // Format times
  const dtStart = formatICSDate(reminderDateTime);
  const dtStamp = formatICSDate(now);
  
  // Calculate alarm time (notifyBefore minutes before event)
  const alarmMinutes = reminder.notifyBefore || 15;
  
  // Build ICS content
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LucidDock//Reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `SUMMARY:${escapeICSText(reminder.title)}`,
  ];

  // Add description if available
  if (reminder.description) {
    icsLines.push(`DESCRIPTION:${escapeICSText(reminder.description)}`);
  }

  // Add location if address is linked
  if (reminder.addressId) {
    icsLines.push(`LOCATION:Address (ID: ${reminder.addressId})`);
  }

  // Add recurrence rule if repeat is set
  if (reminder.repeat && reminder.repeat !== 'none') {
    const rrule = getRecurrenceRule(reminder.repeat);
    if (rrule) {
      icsLines.push(`RRULE:${rrule}`);
    }
  }

  // Add alarm/reminder
  icsLines.push(
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeICSText(reminder.title)}`,
    `TRIGGER:-PT${alarmMinutes}M`,
    'END:VALARM'
  );

  // Close event and calendar
  icsLines.push(
    'END:VEVENT',
    'END:VCALENDAR'
  );

  return icsLines.join('\r\n');
}

/**
 * Escape special characters for ICS format
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Convert reminder repeat to RRULE format
 */
function getRecurrenceRule(repeat: string): string | null {
  switch (repeat) {
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekly':
      return 'FREQ=WEEKLY';
    case 'monthly':
      return 'FREQ=MONTHLY';
    case 'yearly':
      return 'FREQ=YEARLY';
    default:
      return null;
  }
}

/**
 * Download ICS file to user's device
 */
export function downloadICSFile(reminder: Reminder): void {
  const icsContent = generateICSContent(reminder);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  // Create temporary download link
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFileName(reminder.title)}.ics`;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download an .ics file for a reminder date (label + date at 9 AM).
 * Used by ReminderBell / ReminderListModal "Add to Calendar" action.
 */
export function downloadReminderICS(label: string, reminderDate: string): void {
  const dt = new Date(reminderDate + 'T09:00:00');
  const formatD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${y}${m}${day}T${h}${mi}00`;
  };
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@luciddock.app`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LucidDock//Reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatD(new Date())}`,
    `DTSTART:${formatD(dt)}`,
    `SUMMARY:${escapeICSText(label)}`,
    `DESCRIPTION:LucidDock reminder for ${escapeICSText(label)}`,
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeICSText(label)}`,
    'TRIGGER:-PT15M',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFileName(label)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download a single .ics file containing multiple reminder events.
 */
export function downloadAllRemindersICS(items: { label: string; reminderDate: string }[]): void {
  if (items.length === 0) return;
  const formatD = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${y}${m}${day}T${h}${mi}00`;
  };
  const now = formatD(new Date());
  const events = items.map(({ label, reminderDate }) => {
    const dt = new Date(reminderDate + 'T09:00:00');
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@luciddock.app`;
    return [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${formatD(dt)}`,
      `SUMMARY:${escapeICSText(label)}`,
      `DESCRIPTION:LucidDock reminder for ${escapeICSText(label)}`,
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:${escapeICSText(label)}`,
      'TRIGGER:-PT15M',
      'END:VALARM',
      'END:VEVENT',
    ].join('\r\n');
  });
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LucidDock//Reminder//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...events,
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'LucidDock_Reminders.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Sanitize filename for safe download
 */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .substring(0, 50);
}
